import React, { useEffect, useState, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useTranslation } from "react-i18next";
import { 
  RefreshCw, Globe, Clock, TrendingUp, Activity, Share2, Bell, 
  Calendar, X, Copy, Check, Mail, BellRing, Code, 
  Sun, Moon, Volume2, VolumeX, HelpCircle, Cloud,
  Languages, Phone, MessageSquare, Sparkles, ArrowUp, ArrowDown, Minus,
  Download, Hash, History, Keyboard, ChevronDown
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";

// Critical components - loaded immediately (above the fold)
import { 
  GuidedTour, TOUR_STEPS, 
  LanguageSelector, LANGUAGES
} from "@/components/modals";

import { 
  MoodOrb,
  detectEmotion,
  HistoryChart,
  RegionalBreakdown
} from "@/components/panels";

// Lazy-loaded components - below the fold panels
const WordCloudPanel = lazy(() => import("@/components/panels/WordCloudPanel").then(m => ({ default: m.WordCloudPanel })));
const EventImpactPanel = lazy(() => import("@/components/panels/EventImpactPanel").then(m => ({ default: m.EventImpactPanel })));
const ForecastPanel = lazy(() => import("@/components/panels/ForecastPanel").then(m => ({ default: m.ForecastPanel })));
const PredictionPanel = lazy(() => import("@/components/panels/PredictionPanel").then(m => ({ default: m.PredictionPanel })));
const SocialPanel = lazy(() => import("@/components/panels/SocialPanel").then(m => ({ default: m.SocialPanel })));
const StockMarketPanel = lazy(() => import("@/components/panels/StockMarketPanel").then(m => ({ default: m.StockMarketPanel })));
const MoodMeterPanel = lazy(() => import("@/components/panels/MoodMeterPanel").then(m => ({ default: m.MoodMeterPanel })));
const WeatherPanel = lazy(() => import("@/components/panels/WeatherPanel").then(m => ({ default: m.WeatherPanel })));
const TimeTravelPanel = lazy(() => import("@/components/panels/TimeTravelPanel").then(m => ({ default: m.TimeTravelPanel })));
const StatsPanel = lazy(() => import("@/components/panels/StatsPanel").then(m => ({ default: m.StatsPanel })));
const NewsTicker = lazy(() => import("@/components/panels/NewsTicker").then(m => ({ default: m.NewsTicker })));

// Lazy-loaded modals - only loaded when opened
const KeyboardShortcutsModal = lazy(() => import("@/components/modals/KeyboardShortcutsModal").then(m => ({ default: m.KeyboardShortcutsModal })));
const MoodHistoryVisualization = lazy(() => import("@/components/modals/MoodHistoryVisualization").then(m => ({ default: m.MoodHistoryVisualization })));
const MoodAnalysisModal = lazy(() => import("@/components/modals/MoodAnalysisModal").then(m => ({ default: m.MoodAnalysisModal })));
const AddWidgetModal = lazy(() => import("@/components/modals/AddWidgetModal").then(m => ({ default: m.AddWidgetModal })));
const ShareModal = lazy(() => import("@/components/modals/ShareModal").then(m => ({ default: m.ShareModal })));
const CompareModal = lazy(() => import("@/components/modals/CompareModal").then(m => ({ default: m.CompareModal })));
const SubscribeModal = lazy(() => import("@/components/modals/SubscribeModal").then(m => ({ default: m.SubscribeModal })));
const WeeklyForecastModal = lazy(() => import("@/components/modals/WeeklyForecastModal").then(m => ({ default: m.WeeklyForecastModal })));
const KeyTopicsModal = lazy(() => import("@/components/modals/KeyTopicsModal"));
const HelpAboutModal = lazy(() => import("@/components/modals/HelpAboutModal").then(m => ({ default: m.HelpAboutModal })));

// Error boundary for graceful panel degradation
import { PanelErrorBoundary } from "@/components/ui/PanelErrorBoundary";

// Loading fallback for lazy components
const PanelLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[100px]">
    <div className="apple-spinner" />
  </div>
);

import { API } from '@/utils/api';
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Ambient sound system using Web Audio API for procedural soundscapes
import { playAmbientSound, stopAmbientSound, initAudioContext } from '@/utils/ambientAudio';

// Request notification permission
const requestNotificationPermission = async () => {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }
  return false;
};

// Send browser notification
const sendNotification = (title, body, color) => {
  if (Notification.permission === "granted") {
    new Notification(title, { body, tag: "mood-update", renotify: true });
  }
};

// Main Mood Display with clickable modal
const MoodDisplay = ({ mood, color, onOpenAnalysis }) => {
  if (!mood) {
    return (
      <div className="mood-display">
        <div className="apple-spinner" />
        <p className="apple-caption mt-4">Analyzing global mood...</p>
      </div>
    );
  }

  const intensity = mood.intensity || 0.5;

  return (
    <div className="mood-display">
      <MoodOrb color={color} intensity={intensity} adjective={mood.adjective} />
      <AnimatePresence mode="wait">
        <motion.div
          key={mood.adjective}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="z-10 text-center mood-content"
        >
          <motion.h1
            className="mood-adjective mood-adjective-clickable"
            data-testid="mood-adjective"
            data-length={mood.adjective.length > 12 ? 'very-long' : mood.adjective.length > 9 ? 'long' : 'normal'}
            onClick={onOpenAnalysis}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{ cursor: 'pointer' }}
          >
            {mood.adjective}
          </motion.h1>
          {/* Pantone removed */}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};


// Source Ticker
const SourceTicker = ({ sources, isAnalyzing }) => (
  <div className="ticker-container">
    <div className="flex items-center px-4 border-r" style={{ borderColor: 'var(--apple-separator)' }}>
      <Activity size={14} style={{ color: 'var(--apple-green)' }} />
      <span className="ml-2 text-xs font-medium" style={{ color: 'var(--apple-text-tertiary)' }}>LIVE</span>
    </div>
    {sources?.length ? (
      <Marquee gradient={false} speed={35} className="flex-1">
        {sources.map((source, idx) => (
          <div key={idx} className="ticker-item">
            <span className={`ticker-dot ${source.status === 'success' ? 'success' : 'error'}`} />
            <span>{source.name}</span>
            <span style={{ color: 'var(--apple-text-tertiary)' }}>·</span>
            <span style={{ color: 'var(--apple-text-tertiary)' }}>{source.region}</span>
          </div>
        ))}
      </Marquee>
    ) : (
      <span className="px-4 text-sm" style={{ color: 'var(--apple-text-tertiary)' }}>
        {isAnalyzing ? 'Fetching sources...' : 'Waiting for data...'}
      </span>
    )}
  </div>
);

// Main App
function App() {
  const { t, i18n } = useTranslation();
  const [currentMood, setCurrentMood] = useState(null);
  const [regionalMoods, setRegionalMoods] = useState([]);
  const [history, setHistory] = useState([]);
  const [sources, setSources] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showMoodAnalysis, setShowMoodAnalysis] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showWeeklyForecast, setShowWeeklyForecast] = useState(false);
  const [showKeyTopics, setShowKeyTopics] = useState(false);
  const [showHelpAbout, setShowHelpAbout] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [prediction, setPrediction] = useState(null);
  const [socialData, setSocialData] = useState(null);
  const [wordCloudData, setWordCloudData] = useState(null);
  const [eventData, setEventData] = useState([]);
  const [articlesData, setArticlesData] = useState([]);
  const [forecastData, setForecastData] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [weatherData, setWeatherData] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(false);
  // Check for first-time user - check immediately on mount
  const [showTour, setShowTour] = useState(() => {
    // Check localStorage synchronously during initialization
    if (typeof window !== 'undefined') {
      const hasSeenTour = localStorage.getItem('hasSeenTour');
      return false; // Never show tour automatically - user can trigger it manually
    }
    return false;
  });
  const [tourStep, setTourStep] = useState(0);
  const [pullRefreshY, setPullRefreshY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const previousMoodRef = useRef(null);
  const audioRef = useRef(null);
  const touchStartY = useRef(0);

  // Initialize settings from localStorage
  useEffect(() => {
    if (localStorage.getItem('pushNotificationsEnabled') === 'true' && Notification.permission === 'granted') {
      setNotificationsEnabled(true);
    }
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) setDarkMode(savedTheme === 'true');
    const savedSound = localStorage.getItem('soundEnabled');
    if (savedSound !== null) setSoundEnabled(savedSound === 'true');
  }, []);

  // Handle tour
  const closeTour = () => {
    setShowTour(false);
    setTourStep(0);
    localStorage.setItem('hasSeenTour', 'true');
  };

  const nextTourStep = () => {
    if (tourStep < TOUR_STEPS.length - 1) {
      setTourStep(s => s + 1);
    } else {
      closeTour();
    }
  };

  const prevTourStep = () => {
    if (tourStep > 0) setTourStep(s => s - 1);
  };

  // Ambient sound management using Web Audio API procedural sounds
  useEffect(() => {
    if (!soundEnabled || !currentMood) {
      stopAmbientSound(1.0); // Fade out over 1 second
      return;
    }
    
    // Initialize audio context on user interaction
    initAudioContext();
    
    // Play ambient sound matching the current mood
    const intensity = currentMood.intensity || 0.5;
    playAmbientSound(currentMood.adjective, intensity, 0.2);
    
    return () => {
      stopAmbientSound(0.5);
    };
  }, [soundEnabled, currentMood?.adjective, currentMood?.intensity]);

  useEffect(() => {
    document.documentElement.classList.toggle('light-mode', !darkMode);
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem('soundEnabled', soundEnabled.toString());
  }, [soundEnabled]);

  // Loading state for initial fetch only
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingStage, setLoadingStage] = useState('');
  const initialLoadDoneRef = useRef(false);
  // True once the first bootstrap response has come back. Lets panels show a
  // graceful empty state for feeds that are simply empty, instead of an
  // indefinite "Loading…" spinner that reads as broken.
  const [dataReady, setDataReady] = useState(false);

  // Define triggerAnalysis early so it can be used in keyboard shortcuts and pull-to-refresh
  const triggerAnalysis = useCallback(async () => {
    try {
      await axios.post(`${API}/mood/analyze`);
      setIsAnalyzing(true);
      setCountdown(REFRESH_INTERVAL);
    } catch (err) {
      console.error('Analysis trigger error:', err);
    }
  }, []);

  // Keyboard shortcuts (must come after triggerAnalysis)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch(e.key.toLowerCase()) {
        case 'r': triggerAnalysis(); break;
        case 'd': setDarkMode(d => !d); break;
        case 's': setSoundEnabled(s => !s); break;
        case 'h': setShowHistory(true); break;
        case 'c': setShowCompare(true); break;
        case '?': setShowKeyboardShortcuts(true); break;
        case 't': setShowTour(true); setTourStep(0); break;
        case 'i': setShowHelpAbout(true); break;
        default: break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [triggerAnalysis]);

  // Pull to refresh for mobile
  const handleTouchStart = (e) => {
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e) => {
    if (touchStartY.current === 0) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;
    if (diff > 0 && window.scrollY === 0) {
      setIsPulling(true);
      setPullRefreshY(Math.min(diff * 0.5, 100));
    }
  };

  const handleTouchEnd = () => {
    if (pullRefreshY > 60) {
      triggerAnalysis();
    }
    setPullRefreshY(0);
    setIsPulling(false);
    touchStartY.current = 0;
  };

  const fetchAll = useCallback(async (silent = false) => {
    try {
      // Only show loading UI on initial load, not on subsequent refreshes
      const showLoading = !initialLoadDoneRef.current && !silent;
      
      if (showLoading) {
        setLoadingStage('Loading...');
        setLoadingProgress(30);
      }
      
      const res = await axios.get(`${API}/bootstrap`, { timeout: 45000 });
      const data = res.data;
      
      if (showLoading) {
        setLoadingProgress(80);
      }
      
      // Extract mood data
      if (data.mood?.current_mood) {
        const newMood = data.mood.current_mood;
        if (previousMoodRef.current && notificationsEnabled && newMood.adjective !== previousMoodRef.current.adjective) {
          sendNotification('Mood Changed', `Now: ${newMood.adjective.toUpperCase()}`, newMood.color);
        }
        previousMoodRef.current = newMood;
        setCurrentMood(newMood);
        setRegionalMoods(data.mood.regional_moods || []);
        if (data.mood.events?.length) {
          setEventData(data.mood.events);
        }
      }
      
      // Set all other data from bootstrap response
      setIsAnalyzing(data.is_analyzing || false);
      setHistory(data.history?.history || []);
      setSources(data.sources?.sources || []);
      setWordCloudData(data.wordcloud || { words: [] });
      if (data.events?.events?.length) {
        setEventData(data.events.events);
      }
      setAlerts(data.alerts || []);
      
      // Always set stocks and forecast if data exists (don't require length check)
      if (data.stocks && data.stocks.indexes) {
        setStockData(data.stocks);
      }
      if (data.forecast && data.forecast.forecast) {
        setForecastData(data.forecast);
      }
      if (data.social) {
        setSocialData(data.social);
      }
      if (data.weather) {
        setWeatherData(data.weather);
      }
      if (data.articles?.length) {
        setArticlesData(data.articles);
      }
      
      if (showLoading) {
        setLoadingProgress(100);
        setLoadingStage('');
      }
      
      // Mark initial load as done
      initialLoadDoneRef.current = true;
      setDataReady(true);
      
      // Fetch prediction separately (LLM-dependent, may not be cached)
      axios.get(`${API}/mood/predict`, { timeout: 15000 })
        .then(predRes => setPrediction(predRes.data))
        .catch(e => console.error('Prediction fetch error:', e));

    } catch (e) {
      console.error('Fetch error:', e);
      setLoadingStage('');
      setLoadingProgress(0);
    }
  }, [notificationsEnabled]);

  useEffect(() => {
    fetchAll();
  }, []);

  // Trigger analysis if no mood exists after initial fetch
  useEffect(() => {
    if (currentMood === null && !isAnalyzing) {
      // Wait a moment for data to settle, then trigger if still no mood
      const timer = setTimeout(() => {
        axios.get(`${API}/bootstrap`, { timeout: 30000 })
          .then(res => {
            if (!res.data?.mood?.current_mood) {
              triggerAnalysis();
            }
          })
          .catch(() => triggerAnalysis());
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentMood, isAnalyzing, triggerAnalysis]);

  // Helper: true when the tab is visible (avoids polling backgrounded tabs)
  const isPageVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

  // Refresh data every 30 seconds to keep components updated (silent refresh).
  // Skips network calls while the tab is hidden to save battery/data and spare the backend.
  useEffect(() => {
    const dataRefresh = setInterval(() => { if (isPageVisible()) fetchAll(true); }, 30000);
    return () => clearInterval(dataRefresh);
  }, [fetchAll]);

  useEffect(() => {
    if (isAnalyzing) {
      const interval = setInterval(() => { if (isPageVisible()) fetchAll(true); }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAnalyzing, fetchAll]);

  useEffect(() => {
    const refresh = setInterval(() => { if (isPageVisible()) triggerAnalysis(); }, REFRESH_INTERVAL);
    const countdown = setInterval(() => setCountdown(p => Math.max(0, p - 1000)), 1000);
    return () => { clearInterval(refresh); clearInterval(countdown); };
  }, [triggerAnalysis]);

  // When the tab becomes visible again, immediately pull fresh data so the
  // dashboard isn't stale after the background intervals were skipped.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') fetchAll(true); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchAll]);

  const formatCountdown = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;
  const exportPDF = () => window.open(`${API}/report/pdf?hours=24`, '_blank');
  const moodColor = currentMood?.color || '#0a84ff';

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-black' : 'bg-gray-50'}`} data-testid="app-container">
      {/* Header */}
      <header className="app-header">
        <div className="header-brand">
          <Globe size={20} style={{ color: moodColor }} />
          <span className="header-title hidden sm:inline">Mood of the World</span>
        </div>
        
        {/* Desktop Controls */}
        <div className="header-controls hidden md:flex">
          <div className="countdown-badge">
            <Clock size={14} />
            <span>{formatCountdown(countdown)}</span>
          </div>
          <button onClick={() => setShowLanguage(true)} className="header-button" data-testid="language-button" aria-label="Change language">
            <Languages size={18} />
          </button>
          <button onClick={() => setDarkMode(!darkMode)} className="header-button" data-testid="theme-toggle" aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button onClick={() => setSoundEnabled(!soundEnabled)} className={`header-button ${soundEnabled ? 'active' : ''}`} data-testid="sound-toggle" aria-label={soundEnabled ? 'Mute ambient sound' : 'Enable ambient sound'} aria-pressed={soundEnabled}>
            {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
          <button onClick={() => setShowAlerts(!showAlerts)} className={`header-button ${alerts.length ? 'active' : ''}`} data-testid="alerts-button" aria-label={`Alerts${alerts.length ? ` (${alerts.length})` : ''}`}>
            <Bell size={18} />
          </button>
          <button onClick={() => setShowSubscribe(true)} className="header-button" data-testid="subscribe-button" aria-label="Subscribe to mood updates">
            <BellRing size={18} />
          </button>
          <button onClick={() => setShowHistory(true)} className="header-button" data-testid="history-viz-button" aria-label="View mood history">
            <History size={18} />
          </button>
          <button onClick={() => setShowCompare(true)} className="header-button" data-testid="compare-button" aria-label="Compare moods over time">
            <Calendar size={18} />
          </button>
          <button onClick={() => setShowShare(true)} className="header-button" data-testid="share-button" aria-label="Share">
            <Share2 size={18} />
          </button>
          <button onClick={() => setShowAddWidget(true)} className="header-button" data-testid="add-widget-button" title="Add Widget to Your Site" aria-label="Add widget to your site">
            <Code size={18} />
          </button>
          <button onClick={() => setShowHelpAbout(true)} className="header-button" data-testid="help-button" title="About & Help" aria-label="About and help">
            <HelpCircle size={18} />
          </button>
          <button onClick={triggerAnalysis} disabled={isAnalyzing} className="header-button" data-testid="refresh-button" aria-label={isAnalyzing ? 'Refreshing…' : 'Refresh now'}>
            <RefreshCw size={18} className={isAnalyzing ? 'animate-spin' : ''} />
          </button>
        </div>
        
        {/* Mobile Controls - Essential buttons + Menu */}
        <div className="header-controls flex md:hidden">
          <button onClick={() => setDarkMode(!darkMode)} className="header-button" data-testid="theme-toggle-mobile" aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button onClick={() => setSoundEnabled(!soundEnabled)} className={`header-button ${soundEnabled ? 'active' : ''}`} data-testid="sound-toggle-mobile" aria-label={soundEnabled ? 'Mute ambient sound' : 'Enable ambient sound'} aria-pressed={soundEnabled}>
            {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
          <button onClick={triggerAnalysis} disabled={isAnalyzing} className="header-button" data-testid="refresh-button-mobile" aria-label={isAnalyzing ? 'Refreshing…' : 'Refresh now'}>
            <RefreshCw size={18} className={isAnalyzing ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="header-button" data-testid="mobile-menu-toggle" aria-label="Menu" aria-expanded={showMobileMenu}>
            <ChevronDown size={18} className={showMobileMenu ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </button>
        </div>
      </header>
      
      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mobile-menu-dropdown md:hidden"
            data-testid="mobile-menu"
          >
            <button onClick={() => { setShowLanguage(true); setShowMobileMenu(false); }} className="mobile-menu-item">
              <Languages size={16} />
              <span>Language</span>
            </button>
            <button onClick={() => { setShowAlerts(!showAlerts); setShowMobileMenu(false); }} className="mobile-menu-item">
              <Bell size={16} />
              <span>Alerts {alerts.length > 0 && `(${alerts.length})`}</span>
            </button>
            <button onClick={() => { setShowSubscribe(true); setShowMobileMenu(false); }} className="mobile-menu-item">
              <BellRing size={16} />
              <span>Subscribe</span>
            </button>
            <button onClick={() => { setShowHistory(true); setShowMobileMenu(false); }} className="mobile-menu-item">
              <History size={16} />
              <span>History</span>
            </button>
            <button onClick={() => { setShowCompare(true); setShowMobileMenu(false); }} className="mobile-menu-item">
              <Calendar size={16} />
              <span>Compare</span>
            </button>
            <button onClick={() => { setShowShare(true); setShowMobileMenu(false); }} className="mobile-menu-item">
              <Share2 size={16} />
              <span>Share</span>
            </button>
            <button onClick={() => { setShowAddWidget(true); setShowMobileMenu(false); }} className="mobile-menu-item">
              <Code size={16} />
              <span>Embed Widget</span>
            </button>
            <button onClick={() => { setShowHelpAbout(true); setShowMobileMenu(false); }} className="mobile-menu-item">
              <HelpCircle size={16} />
              <span>Help & About</span>
            </button>
            <button onClick={() => { setShowTour(true); setTourStep(0); setShowMobileMenu(false); }} className="mobile-menu-item">
              <Sparkles size={16} />
              <span>Guided Tour</span>
            </button>
            <button onClick={() => { setShowKeyboardShortcuts(true); setShowMobileMenu(false); }} className="mobile-menu-item">
              <Keyboard size={16} />
              <span>Shortcuts</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guided Tour */}
      <GuidedTour 
        isOpen={showTour}
        onClose={closeTour}
        steps={TOUR_STEPS}
        currentStep={tourStep}
        onNext={nextTourStep}
        onPrev={prevTourStep}
        moodColor={moodColor}
      />

      {/* Pull to Refresh Indicator */}
      <AnimatePresence>
        {isPulling && (
          <motion.div
            className="pull-refresh-indicator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: pullRefreshY }}
            exit={{ opacity: 0 }}
          >
            <ChevronDown size={24} className={pullRefreshY > 60 ? 'text-green-500' : ''} />
            <span>{pullRefreshY > 60 ? t('releaseToRefresh') : t('pullToRefresh')}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Alerts Dropdown */}
      <AnimatePresence>
        {showAlerts && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-16 right-4 z-50 w-72 apple-card p-4"
            data-testid="alerts-dropdown"
          >
            <div className="flex justify-between items-center mb-3">
              <span className="apple-caption">{t('alerts')}</span>
              <button onClick={() => setShowAlerts(false)}><X size={16} /></button>
            </div>
            {alerts.length ? alerts.map((a, i) => (
              <div key={i} className="p-3 rounded-xl mb-2" style={{ background: a.severity === 'high' ? 'rgba(255,69,58,0.15)' : 'var(--apple-bg-secondary)' }}>
                <p className="text-sm">{a.message}</p>
              </div>
            )) : <p className="text-sm text-center py-4" style={{ color: 'var(--apple-text-tertiary)' }}>{t('noAlerts')}</p>}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Grid */}
      <main 
        className="dashboard-grid pt-20"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Main Mood Display */}
        <section className="main-panel apple-card p-4 sm:p-8" data-testid="main-display">
          <MoodDisplay
            mood={currentMood}
            color={moodColor} 
            onOpenAnalysis={() => setShowMoodAnalysis(true)}
          />
        </section>

        {/* History */}
        <section className="side-panel apple-card p-4 sm:p-5" data-testid="history-panel" onClick={() => setShowHistory(true)} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}>
          <div className="panel-header" style={{ flexShrink: 0 }}>
            <TrendingUp size={16} className="panel-icon" />
            <span className="panel-title">{t('history')}</span>
          </div>
          <HistoryChart history={history} darkMode={darkMode} currentMoodColor={moodColor} />
        </section>

        {/* Regional */}
        <section className="side-panel apple-card p-5" data-testid="regional-panel">
          <div className="panel-header">
            <Globe size={16} className="panel-icon" />
            <span className="panel-title">{t('regional')}</span>
          </div>
          <PanelErrorBoundary panelName="Regional Breakdown" minimal>
            <RegionalBreakdown regionalMoods={regionalMoods} />
          </PanelErrorBoundary>
        </section>

        {/* Word Cloud */}
        <section className="side-panel apple-card p-4 sm:p-5" data-testid="word-cloud-panel">
          <div className="panel-header">
            <Cloud size={16} className="panel-icon" />
            <span className="panel-title">{t('keyTopics')}</span>
          </div>
          <PanelErrorBoundary panelName="Key Topics">
            <Suspense fallback={<PanelLoader />}>
              <WordCloudPanel
                wordCloudData={wordCloudData}
                moodColor={moodColor}
                loaded={dataReady}
                onTopicClick={(topic) => {
                  setSelectedTopic(topic);
                  setShowKeyTopics(true);
                }}
              />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* Event Impact - NEW */}
        <section className="side-panel apple-card p-4 sm:p-5" data-testid="event-impact-panel">
          <div className="panel-header">
            <Activity size={16} className="panel-icon" />
            <span className="panel-title">{t('eventImpact')}</span>
          </div>
          <PanelErrorBoundary panelName="Event Impact">
            <Suspense fallback={<PanelLoader />}>
              <EventImpactPanel events={eventData} moodColor={moodColor} />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* Prediction */}
        <section className="bottom-panel apple-card" data-testid="prediction-section">
          <PanelErrorBoundary panelName="AI Prediction">
            <Suspense fallback={<PanelLoader />}>
              <PredictionPanel prediction={prediction} onOpenForecast={() => setShowWeeklyForecast(true)} />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* 24h Forecast - NEW */}
        <section className="bottom-panel apple-card p-4" data-testid="forecast-section">
          <div className="panel-header mb-2">
            <Clock size={16} className="panel-icon" />
            <span className="panel-title">{t('forecast')}</span>
          </div>
          <PanelErrorBoundary panelName="24H Forecast">
            <Suspense fallback={<PanelLoader />}>
              <ForecastPanel forecast={forecastData} moodColor={moodColor} loaded={dataReady} />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* Stock Market Correlation - NEW */}
        <section className="bottom-panel apple-card p-4" data-testid="stock-market-section">
          <PanelErrorBoundary panelName="Mood vs Markets">
            <Suspense fallback={<PanelLoader />}>
              <StockMarketPanel moodData={currentMood} stockData={stockData} loaded={dataReady} />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* Mood Meter Matrix - NEW */}
        <section className="bottom-panel apple-card p-4" data-testid="mood-meter-section">
          <PanelErrorBoundary panelName="Mood Matrix">
            <Suspense fallback={<PanelLoader />}>
              <MoodMeterPanel currentMood={currentMood} />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* Time Travel - NEW */}
        <section className="bottom-panel apple-card p-4" data-testid="time-travel-section">
          <PanelErrorBoundary panelName="Time Travel">
            <Suspense fallback={<PanelLoader />}>
              <TimeTravelPanel history={history} />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* Weather Influence - NEW */}
        <section className="bottom-panel apple-card p-4" data-testid="weather-section">
          <PanelErrorBoundary panelName="Weather">
            <Suspense fallback={<PanelLoader />}>
              <WeatherPanel weatherData={weatherData} />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* Social */}
        <section className="bottom-panel apple-card" data-testid="social-section">
          <PanelErrorBoundary panelName="Social Sentiment">
            <Suspense fallback={<PanelLoader />}>
              <SocialPanel socialData={socialData} />
            </Suspense>
          </PanelErrorBoundary>
        </section>

        {/* Stats */}
        <section className="bottom-panel apple-card" data-testid="stats-section">
          <Suspense fallback={<PanelLoader />}>
            <StatsPanel mood={currentMood} isAnalyzing={isAnalyzing} onExportPDF={exportPDF} />
          </Suspense>
        </section>
      </main>

      {/* Copyright Footer - above tickers */}
      <footer className="copyright-footer" data-testid="copyright-footer">
        <span>© 2026 Cooper, Levy & Partners, LLC</span>
      </footer>

      {/* Ticker */}
      <SourceTicker sources={sources} isAnalyzing={isAnalyzing} />
      
      {/* News Articles Ticker */}
      <Suspense fallback={null}>
        <NewsTicker moodColor={moodColor} />
      </Suspense>

      {/* Modals - only load when opened */}
      <Suspense fallback={null}>
        {showShare && <ShareModal mood={currentMood} isOpen={showShare} onClose={() => setShowShare(false)} />}
        {showCompare && <CompareModal isOpen={showCompare} onClose={() => setShowCompare(false)} />}
        {showSubscribe && <SubscribeModal isOpen={showSubscribe} onClose={() => setShowSubscribe(false)} notificationsEnabled={notificationsEnabled} setNotificationsEnabled={setNotificationsEnabled} />}
        {showHistory && <MoodHistoryVisualization history={history} isOpen={showHistory} onClose={() => setShowHistory(false)} />}
        {showMoodAnalysis && <MoodAnalysisModal mood={currentMood} color={moodColor} events={eventData} keywords={wordCloudData?.words} isOpen={showMoodAnalysis} onClose={() => setShowMoodAnalysis(false)} />}
        {showKeyboardShortcuts && <KeyboardShortcutsModal isOpen={showKeyboardShortcuts} onClose={() => setShowKeyboardShortcuts(false)} />}
        {showAddWidget && <AddWidgetModal isOpen={showAddWidget} onClose={() => setShowAddWidget(false)} currentMood={currentMood} />}
        {showWeeklyForecast && <WeeklyForecastModal isOpen={showWeeklyForecast} onClose={() => setShowWeeklyForecast(false)} prediction={prediction} />}
        {showKeyTopics && <KeyTopicsModal topic={selectedTopic} articles={articlesData} events={eventData} moodColor={moodColor} isOpen={showKeyTopics} onClose={() => setShowKeyTopics(false)} />}
        {showHelpAbout && <HelpAboutModal isOpen={showHelpAbout} onClose={() => setShowHelpAbout(false)} moodColor={moodColor} />}
      </Suspense>
      
      {/* Language Selector - critical, not lazy */}
      <LanguageSelector isOpen={showLanguage} onClose={() => setShowLanguage(false)} />
    </div>
  );
}

export default App;
